import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  MagnifyingGlass,
  CaretDown,
  X,
  PencilSimple,
} from '@phosphor-icons/react';
import { Truck } from '../../types/dbTypes';
import { TruckAttributionWithTruck } from '../../types/apiTypes';
import { ClipLoader } from 'react-spinners';
import { Tooltip } from 'antd';
import { formatCentsToDollars } from '../../utils/attributionUtils';

interface TruckMultiSelectProps {
  trucks: Truck[];
  selectedTruckIds: string[];
  onSelectionChange: (truckIds: string[]) => void;
  onSingleSave: (truckId: string) => Promise<void>;
  onOpenSplitModal: () => void;
  /**
   * Trucks that may already be attributed to the transaction but are NOT
   * offered as new options — e.g. a deleted truck. Used only to resolve a
   * selected id to a name (rendered with a "(Deleted)" tag) so historical
   * attribution stays visible; never shown in the selectable list.
   */
  additionalTrucks?: Truck[];
  /**
   * Assign the transaction to the business (no truck). Business is a valid
   * destination, not an error — selecting it clears any truck attribution
   * (nulled on the backend). When omitted, the Business option is hidden.
   */
  onAssignBusiness?: () => Promise<void>;
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  /** Existing attributions for the transaction (to show amounts and enable "Edit" mode) */
  existingAttributions?: TruckAttributionWithTruck[];
}

const TruckMultiSelect: React.FC<TruckMultiSelectProps> = ({
  trucks,
  selectedTruckIds,
  onSelectionChange,
  onSingleSave,
  onOpenSplitModal,
  onAssignBusiness,
  additionalTrucks = [],
  disabled = false,
  isLoading = false,
  placeholder = 'Select Truck',
  existingAttributions = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Check if there are existing multi-truck attributions
  const hasExistingAttributions = existingAttributions.length > 1;

  // Build a map of truck_id -> amount (in cents) for quick lookup
  // Note: API returns amounts in dollars, so we convert to cents here
  const attributionAmountMap = useMemo(() => {
    const map: Record<string, number> = {};
    existingAttributions.forEach((attr) => {
      if (attr.truck_id) {
        map[attr.truck_id] = Math.round((attr.amount ?? 0) * 100); // Convert dollars to cents
      }
    });
    return map;
  }, [existingAttributions]);

  // Filter trucks based on search query
  const filteredTrucks = useMemo(() => {
    if (!searchQuery.trim()) return trucks;
    const query = searchQuery.toLowerCase();
    return trucks.filter((truck) =>
      truck.truck_display_name?.toLowerCase().includes(query)
    );
  }, [trucks, searchQuery]);

  // Resolve a selected truck_id to a display label. Active trucks (in the
  // selectable `trucks` list) resolve to their name. A selected id that isn't
  // in that list — e.g. a deleted truck still attributed to this transaction —
  // resolves from `additionalTrucks` so the history stays visible; it is never
  // offered in the dropdown, so it can't be reassigned. The "(Deleted)" tag is
  // driven by the record's own `is_deleted` flag, not by mere absence from
  // `trucks` (a trucks-query error empties that list, which must not turn every
  // row into a false "(Deleted)"). An id we can't resolve at all — e.g. a legacy
  // truck_id-only attribution to a since-deleted truck with no record on the
  // payload — reads "Unknown truck" rather than silently collapsing to Business.
  const resolveTruckLabel = useCallback(
    (truckId: string): string => {
      const active = trucks.find((t) => t.truck_id === truckId);
      if (active) return active.truck_display_name ?? 'Truck';
      const gone = additionalTrucks.find((t) => t.truck_id === truckId);
      if (gone) {
        const name = gone.truck_display_name ?? 'Truck';
        return gone.is_deleted ? `${name} (Deleted)` : name;
      }
      return 'Unknown truck';
    },
    [trucks, additionalTrucks]
  );

  // Get display names for selected trucks
  const selectedTruckNames = useMemo(() => {
    return selectedTruckIds.map(resolveTruckLabel).filter(Boolean);
  }, [selectedTruckIds, resolveTruckLabel]);

  // Update dropdown position - memoized to prevent recreating on every render
  const updateDropdownPosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 300; // Approximate max height of dropdown

      // Determine if dropdown should open upward or downward
      const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      // Calculate horizontal position with viewport bounds checking
      const viewportWidth = window.innerWidth;
      const padding = 8; // Minimum padding from screen edges
      const maxAvailableWidth = viewportWidth - padding * 2;

      // Calculate dropdown width (minimum 240px, but respect viewport constraints)
      const idealWidth = Math.max(rect.width, 240);
      const dropdownWidth = Math.min(idealWidth, maxAvailableWidth);

      let leftPosition = rect.left;

      // Check if dropdown would overflow on the right
      if (leftPosition + dropdownWidth > viewportWidth - padding) {
        // Align to right edge of viewport with padding
        leftPosition = viewportWidth - dropdownWidth - padding;
      }

      // Check if dropdown would overflow on the left
      if (leftPosition < padding) {
        leftPosition = padding;
      }

      // Calculate dropdown position
      const styles: React.CSSProperties = {
        position: 'fixed',
        left: `${leftPosition}px`,
        width: `${dropdownWidth}px`,
        zIndex: 9999,
      };

      if (openUpward) {
        styles.bottom = `${window.innerHeight - rect.top + 4}px`;
      } else {
        styles.top = `${rect.bottom + 4}px`;
      }

      setDropdownStyles(styles);
    }
  }, []);

  const handleToggle = () => {
    if (!disabled && !isLoading) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      if (!newIsOpen) {
        setSearchQuery('');
      } else {
        updateDropdownPosition();
      }
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Update dropdown position on scroll and resize
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);

      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleTruckToggle = (truckId: string) => {
    if (selectedTruckIds.includes(truckId)) {
      onSelectionChange(selectedTruckIds.filter((id) => id !== truckId));
      return;
    }
    // When everything currently selected is resolve-only (a deleted/unavailable
    // truck kept for history, never offerable), picking a live truck REPLACES it
    // rather than unioning — otherwise the footer flips to a "Split by 2" whose
    // modal silently drops the unofferable truck. A real live selection still
    // unions normally to build a split.
    const allResolveOnly = selectedTruckIds.every(
      (id) => !trucks.some((t) => t.truck_id === id)
    );
    if (selectedTruckIds.length > 0 && allResolveOnly) {
      onSelectionChange([truckId]);
      return;
    }
    onSelectionChange([...selectedTruckIds, truckId]);
  };

  const handleSingleSave = async () => {
    if (selectedTruckIds.length === 1) {
      setIsSaving(true);
      try {
        await onSingleSave(selectedTruckIds[0]);
        setIsOpen(false);
        setSearchQuery('');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const [isAssigningBusiness, setIsAssigningBusiness] = useState(false);
  const handleAssignBusiness = async () => {
    if (!onAssignBusiness) return;
    setIsAssigningBusiness(true);
    try {
      await onAssignBusiness();
      setIsOpen(false);
      setSearchQuery('');
    } finally {
      setIsAssigningBusiness(false);
    }
  };

  const handleSplitClick = () => {
    setIsOpen(false);
    setSearchQuery('');
    onOpenSplitModal();
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Clearing means "assign to Business" under this model, so persist it
    // rather than only clearing local state — a local-only clear looks saved
    // (the trigger renders "Business") but reverts on refresh. Fall back to a
    // local clear when Business isn't an available destination.
    if (onAssignBusiness) {
      void handleAssignBusiness();
      return;
    }
    onSelectionChange([]);
  };

  // Determine display text
  const getDisplayText = () => {
    if (selectedTruckIds.length === 0) {
      return placeholder;
    }
    if (selectedTruckIds.length === 1) {
      const truckName = selectedTruckNames[0] || placeholder;
      // If there's an existing attribution with amount, show it
      const amount = attributionAmountMap[selectedTruckIds[0]];
      if (amount && amount > 0) {
        return `${truckName}`;
      }
      return truckName;
    }
    // Multiple trucks selected - show "Multiple" with total if attributions exist
    if (hasExistingAttributions) {
      return `Multiple`;
    }
    return 'Multiple';
  };

  // Determine button text
  const getButtonText = () => {
    if (selectedTruckIds.length === 1) {
      return 'Save';
    }
    if (hasExistingAttributions) {
      return 'Edit Attribution';
    }
    const truckIds = new Set(selectedTruckIds);
    return `Split by Units (${truckIds.size})`;
  };

  const displayText = getDisplayText();
  const hasSelection = selectedTruckIds.length > 0;
  // A lone selection that isn't an offerable truck (a deleted truck kept for
  // history, or an otherwise unresolvable id) has nothing to Save — re-saving
  // would just write the stale id back. Suppress the Save button for it.
  const soleSelectionIsResolveOnly =
    selectedTruckIds.length === 1 &&
    !trucks.some((t) => t.truck_id === selectedTruckIds[0]);
  // The trigger truncates with "…" (deleted-truck labels like "UNit 4 (Deleted)"
  // get long); expose the full text on hover, same as the category picker. Show
  // every selected name even when the trigger collapses to "Multiple".
  const tooltipText =
    selectedTruckNames.length > 0 ? selectedTruckNames.join(', ') : displayText;
  // Where Business is an available destination, an empty selection means the
  // transaction rests under Business — a real value, not a blank prompt — so
  // the trigger renders it in the selected style rather than muted placeholder
  // gray.
  const showAsValue = hasSelection || !!onAssignBusiness;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Trigger Button */}
      <Tooltip
        title={isOpen ? '' : tooltipText}
        placement="top"
        mouseEnterDelay={0.3}
      >
        <button
          ref={triggerRef}
          type="button"
          onClick={handleToggle}
          disabled={disabled || isLoading}
          className={`flex h-[32px] w-full items-center justify-between rounded-[4px] border bg-white px-3 text-[14px] transition-all ${isOpen ? 'border-primary ring-2 ring-primary/10' : 'border-[#E5E7EB]'} ${disabled || isLoading ? 'cursor-not-allowed bg-[#F9FAFB] opacity-60' : 'cursor-pointer hover:border-[#D1D5DB]'} `}
        >
          <span
            className={`truncate text-[13px] ${showAsValue ? 'font-medium text-primary' : 'text-[#9CA3AF]'}`}
          >
            {displayText}
          </span>
          <div className="flex items-center gap-1.5">
            {hasSelection && !disabled && !isLoading && (
              <span
                data-testid="truck-clear"
                className="flex h-4 w-4 items-center justify-center rounded-full bg-[#F3F4F6] text-[#9CA3AF] transition-colors hover:bg-[#E5E7EB] hover:text-[#6B7280]"
                onClick={handleClearSelection}
              >
                <X size={10} weight="bold" />
              </span>
            )}
            {isLoading ? (
              <ClipLoader size={12} color="#9CA3AF" />
            ) : (
              <CaretDown
                size={14}
                weight="bold"
                className={`text-[#9CA3AF] transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            )}
          </div>
        </button>
      </Tooltip>

      {/* Dropdown - Rendered via Portal */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyles}
            className="overflow-hidden rounded-[6px] border border-[#E5E7EB] bg-white shadow-lg"
          >
            {/* Search Input */}
            <div className="border-b border-[#F0F1F3] p-2">
              <div className="flex items-center gap-2 rounded-[4px] border border-[#E5E7EB] bg-[#F9FAFB] px-2.5 py-1.5 transition-all focus-within:border-primary focus-within:bg-white focus-within:ring-2 focus-within:ring-primary/10">
                <MagnifyingGlass size={14} className="text-[#9CA3AF]" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search trucks..."
                  className="w-full border-none bg-transparent text-[13px] text-primary outline-none placeholder:text-[#9CA3AF]"
                />
              </div>
            </div>

            {/* Business (no-truck) destination — a first-class bucket, not a
                fallback. Single-select: choosing it clears any truck
                attribution (nulled on the backend). Shown as current when
                nothing is selected. */}
            {onAssignBusiness && (
              <button
                type="button"
                onClick={handleAssignBusiness}
                disabled={isAssigningBusiness}
                className={`flex w-full items-center gap-2.5 border-b border-[#F0F1F3] px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  !hasSelection ? 'bg-[#F0F7FF]' : 'hover:bg-[#F9FAFB]'
                }`}
              >
                <div
                  className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[3px] border transition-all ${
                    !hasSelection
                      ? 'border-primary bg-primary'
                      : 'border-[#D1D5DB] bg-white'
                  }`}
                >
                  {isAssigningBusiness ? (
                    <ClipLoader
                      size={8}
                      color={!hasSelection ? '#ffffff' : '#9CA3AF'}
                    />
                  ) : (
                    !hasSelection && (
                      <Check size={10} className="text-white" weight="bold" />
                    )
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span
                    className={`truncate text-[13px] ${
                      !hasSelection
                        ? 'font-medium text-primary'
                        : 'text-[#374151]'
                    }`}
                  >
                    Business
                  </span>
                  <span className="truncate text-[11px] text-[#9CA3AF]">
                    Not assigned to a truck
                  </span>
                </div>
              </button>
            )}

            {/* Truck List */}
            <div className="max-h-[200px] overflow-y-auto py-1">
              {filteredTrucks.length === 0 ? (
                <div className="px-3 py-6 text-center text-[13px] text-[#9CA3AF]">
                  No trucks found
                </div>
              ) : (
                filteredTrucks.map((truck) => {
                  const isSelected = selectedTruckIds.includes(truck.truck_id);
                  const attributionAmount =
                    attributionAmountMap[truck.truck_id];
                  const hasAmount = attributionAmount && attributionAmount > 0;

                  return (
                    <button
                      key={truck.truck_id}
                      type="button"
                      onClick={() => handleTruckToggle(truck.truck_id)}
                      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                        isSelected ? 'bg-[#F0F7FF]' : 'hover:bg-[#F9FAFB]'
                      }`}
                    >
                      {/* Checkbox */}
                      <div
                        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[3px] border transition-all ${
                          isSelected
                            ? 'border-primary bg-primary'
                            : 'border-[#D1D5DB] bg-white hover:border-[#9CA3AF]'
                        }`}
                      >
                        {isSelected && (
                          <Check
                            size={10}
                            className="text-white"
                            weight="bold"
                          />
                        )}
                      </div>

                      {/* Truck name and amount */}
                      <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                        <span
                          className={`truncate text-[13px] ${isSelected ? 'font-medium text-primary' : 'text-[#374151]'}`}
                        >
                          {truck.truck_display_name}
                        </span>
                        {hasAmount && (
                          <span className="flex-shrink-0 rounded-full bg-[#F3F4F6] px-2 py-0.5 text-[11px] font-medium tabular-nums text-[#6B7280]">
                            {formatCentsToDollars(attributionAmount)}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer Actions */}
            <div className="border-t border-[#F0F1F3] p-2">
              {selectedTruckIds.length === 0 ? (
                <div className="py-2 text-center text-[12px] text-[#9CA3AF]">
                  Select one or more trucks
                </div>
              ) : soleSelectionIsResolveOnly ? (
                <div className="py-2 text-center text-[12px] text-[#9CA3AF]">
                  Truck no longer available — kept for history
                </div>
              ) : selectedTruckIds.length === 1 ? (
                <button
                  type="button"
                  onClick={handleSingleSave}
                  disabled={isSaving}
                  className="flex w-full items-center justify-center gap-2 rounded-[4px] bg-primary px-3 py-2 text-[13px] font-medium text-white transition-all hover:bg-[#2A2F4F] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSaving ? <ClipLoader size={12} color="#ffffff" /> : 'Save'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSplitClick}
                  className="flex w-full items-center justify-center gap-2 rounded-[4px] bg-secondary px-3 py-2 text-[13px] font-medium text-white transition-all hover:bg-[#5A8558] disabled:opacity-60"
                >
                  {hasExistingAttributions && (
                    <PencilSimple size={14} weight="bold" />
                  )}
                  {getButtonText()}
                </button>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TruckMultiSelect;
